import { useState } from "react";
import { Container } from "react-bootstrap";
import Particle from "../Particle";
import { services } from "../../data/projects";
import styled, { keyframes } from "styled-components";

// ─── Keyframes ───────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalSlideIn = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Section ─────────────────────────────────────────────────
const Section = styled.section`
  position: relative;
  padding: 80px 0 100px;
  background-image: var(--bg-alt);
  background-color: var(--bg);
  color: var(--text-primary);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -200px; left: 50%;
    transform: translateX(-50%);
    width: 800px; height: 800px;
    background: radial-gradient(circle, rgba(199,112,240,0.07) 0%, transparent 70%);
    pointer-events: none;
  }
`;

// ─── Header ──────────────────────────────────────────────────
const Header = styled.div`
  text-align: center;
  margin-bottom: 70px;
  animation: ${fadeUp} 0.8s ease both;


  h1 {
    font-size: clamp(2rem, 5vw, 3.2rem);
    font-weight: 800;
    color: var(--text-primary);
    line-height: 1.2;
    margin-bottom: 16px;

    .purple {
      background: linear-gradient(135deg, var(--primary-color), var(--secondary-color), var(--accent-color));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .subtitle {
    font-size: 1.05em;
    color: var(--muted);
    font-weight: 400;
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

// ─── Grid ─────────────────────────────────────────────────────
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); gap: 22px; }
  @media (max-width: 600px)  { grid-template-columns: 1fr; gap: 18px; }
`;

// ─── Card ─────────────────────────────────────────────────────
const CardWrapper = styled.div`
  animation: ${fadeUp} 0.7s ease both;
  animation-delay: ${({ $index }) => $index * 0.1}s;
  height: 100%;
`;

const Card = styled.div`
  position: relative;
  padding: 32px 28px 28px;
  background: var(--card-bg);
  border-radius: 20px;
  border: 1px solid var(--card-border);
  backdrop-filter: blur(16px);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1),
              box-shadow 0.3s ease,
              border-color 0.3s ease;
  overflow: hidden;

  /* Ambient glow layer */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background: radial-gradient(
      circle at 30% 20%,
      ${({ $color }) => $color}12 0%,
      transparent 60%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: ${({ $color }) => $color}70;
    box-shadow:
      0 20px 60px ${({ $color }) => $color}20,
      0 0 0 1px ${({ $color }) => $color}25;

    &::before { opacity: 1; }
  }
`;

// ─── Number Badge ─────────────────────────────────────────────
const CardNumber = styled.span`
  position: absolute;
  top: 20px; right: 22px;
  font-size: 0.7em;
  font-weight: 700;
  letter-spacing: 1px;
  color: ${({ $color }) => $color};
  opacity: 0.35;
  font-variant-numeric: tabular-nums;
`;

// ─── Icon ─────────────────────────────────────────────────────
const IconWrapper = styled.div`
  width: 72px; height: 72px;
  border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $color }) => `${$color}15`};
  border: 1.5px solid ${({ $color }) => `${$color}35`};
  font-size: 2em;
  transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1);
  flex-shrink: 0;

  ${Card}:hover & {
    background: ${({ $color }) => `${$color}28`};
    border-color: ${({ $color }) => `${$color}70`};
    transform: scale(1.12) rotate(-4deg);
    box-shadow: 0 0 30px ${({ $color }) => `${$color}40`};
  }
`;

// ─── Content ──────────────────────────────────────────────────
const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardTitle = styled.h3`
  font-size: 1.25em;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;
  transition: color 0.3s;

  ${Card}:hover & {
    color: ${({ $color }) => $color};
  }
`;

const CardDesc = styled.p`
  font-size: 0.88em;
  color: var(--text-secondary);
  line-height: 1.65;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// ─── Footer ───────────────────────────────────────────────────
const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid var(--card-border);
  margin-top: auto;
`;

const LearnMore = styled.span`
  font-size: 0.82em;
  font-weight: 600;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.3px;
  transition: gap 0.3s ease;

  ${Card}:hover & { gap: 10px; }

  .arrow {
    display: inline-block;
    transition: transform 0.3s;
  }

  ${Card}:hover & .arrow { transform: translateX(4px); }
`;

const DetailCount = styled.span`
  font-size: 0.75em;
  color: var(--muted);
  background: var(--surface);
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid var(--card-border);
`;

// ─── Modal Overlay ────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(10px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.25s ease both;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 620px;
  max-height: 85vh;
  overflow-y: auto;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 24px;
  animation: ${modalSlideIn} 0.35s cubic-bezier(0.34,1.56,0.64,1) both;
  position: relative;

  scrollbar-width: thin;
  scrollbar-color: var(--primary-color) transparent;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 10px;
    opacity: 0.4;
  }
`;

const ModalTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 28px 32px 24px;
  background: var(--bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  align-items: flex-start;
  gap: 18px;
`;

const ModalIcon = styled.div`
  width: 60px; height: 60px;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $color }) => `${$color}18`};
  border: 1.5px solid ${({ $color }) => `${$color}40`};
  font-size: 1.8em;
  flex-shrink: 0;
`;

const ModalTitleGroup = styled.div`
  flex: 1;

  h2 {
    font-size: 1.5em;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 0 0 6px;
  }

  p {
    font-size: 0.88em;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.55;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 22px; right: 22px;
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1px solid var(--card-border);
  background: var(--surface);
  color: var(--muted);
  font-size: 1em;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: white;
  }
`;

const ModalBody = styled.div`
  padding: 28px 32px;
`;

const SectionLabel = styled.p`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: var(--primary-color);
  margin: 0 0 16px;
  opacity: 0.85;
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0; margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.9em;
  color: var(--text-secondary);
  line-height: 1.55;
  padding: 12px 16px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: ${({ $color }) => `${$color}10`};
    border-color: ${({ $color }) => `${$color}35`};
    color: var(--text-primary);
  }

  &::before {
    content: '✦';
    color: ${({ $color }) => $color};
    font-size: 0.65em;
    margin-top: 3px;
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

const ModalFooter = styled.div`
  padding: 0 32px 28px;
  display: flex;
  justify-content: flex-end;
`;

const BtnClose = styled.button`
  padding: 10px 28px;
  border-radius: 10px;
  border: 1px solid var(--card-border);
  background: var(--surface);
  color: var(--text-secondary);
  font-size: 0.88em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: white;
  }
`;

// ─── Component ────────────────────────────────────────────────
function Services() {
  const [activeService, setActiveService] = useState(null);

  return (
    <Section>
      <Particle />
      <Container>
        <Header>
          <h1>
            Services I <span className="purple">Offer</span>
          </h1>
          <p className="subtitle">
            Comprehensive development solutions crafted with precision and purpose
          </p>
        </Header>

        <Grid>
          {services.map((service, index) => (
            <CardWrapper key={service.id} $index={index}>
              <Card $color={service.color} onClick={() => setActiveService(service)}>
                <CardNumber $color={service.color}>
                  {String(index + 1).padStart(2, "0")}
                </CardNumber>

                <IconWrapper $color={service.color}>
                  {service.icon}
                </IconWrapper>

                <CardContent>
                  <CardTitle $color={service.color}>{service.title}</CardTitle>
                  <CardDesc>{service.description}</CardDesc>
                </CardContent>

                <CardFooter>
                  <LearnMore $color={service.color}>
                    Learn more <span className="arrow">→</span>
                  </LearnMore>
                  {service.details?.length > 0 && (
                    <DetailCount>{service.details.length} features</DetailCount>
                  )}
                </CardFooter>
              </Card>
            </CardWrapper>
          ))}
        </Grid>
      </Container>

      {/* ── Custom Modal ── */}
      {activeService && (
        <Overlay onClick={() => setActiveService(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTop>
              <ModalIcon $color={activeService.color}>
                {activeService.icon}
              </ModalIcon>
              <ModalTitleGroup $color={activeService.color}>
                <h2>{activeService.title}</h2>
                <p>{activeService.description}</p>
              </ModalTitleGroup>
              <CloseBtn onClick={() => setActiveService(null)}>✕</CloseBtn>
            </ModalTop>

            <ModalBody>
              <SectionLabel>What's included</SectionLabel>
              <DetailsList>
                {activeService.details?.map((item, i) => (
                  <DetailItem key={i} $color={activeService.color}>
                    {item}
                  </DetailItem>
                ))}
              </DetailsList>
            </ModalBody>

            <ModalFooter>
              <BtnClose onClick={() => setActiveService(null)}>Close</BtnClose>
            </ModalFooter>
          </ModalBox>
        </Overlay>
      )}
    </Section>
  );
}

export default Services;